import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { PipeLineType } from 'config'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { filterPipeline } from 'services'

import { DEFAULT_LOAN_QUERY_TYPE, LoanQueryType } from './constants'
import { LoanSearchResults } from './LoanSearchResults'
import { isServicingLoan, normalizePipelineLoans } from './utils'

// Results per request. `Show more` asks for another page of this size, and the pipeline query
// caps a single page at 50 however much is asked for.
const PAGE_SIZE = 15

export const GlobalLoanSearch = ({ className = 'w-36 lg:w-40' }: { className?: string }) => {
  const history = useHistory()
  // The Origination/Servicing dropdown the user works from; it is the app-wide mode, so it
  // decides which side of the platform a search result opens on.
  const pipelineType = useSelector((state: any) => state.pipelineFilters?.pipelineType)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const [query, setQuery] = useState('')
  const [queryType, setQueryType] = useState<LoanQueryType>(DEFAULT_LOAN_QUERY_TYPE)
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [loans, setLoans] = useState<Record<string, any>[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  // Which search the rows on screen belong to. A `Show more` page that arrives after the term
  // changed is dropped instead of being appended to a different search's results.
  const requestKey = useRef('')

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedQuery(query.trim()), 700)
    return () => clearTimeout(timeoutId)
  }, [query])

  // Only a new search moves the highlight back to the top. Appending a page must not, or
  // `Show more` would throw the keyboard selection away every time it loads.
  useEffect(() => {
    setActiveIndex(0)
  }, [debouncedQuery, queryType])

  // Global search is deliberately view-independent: it always queries the whole book
  // (origination + servicing), so the same term finds the same loans no matter which pipeline
  // the user happens to be on. It is ranked by how closely each loan matches the term, because
  // only the first page is on screen: sorting by a date stamp left the order as loan number
  // descending, which buried an address match under loans that merely shared a street name.
  const searchFilters = useMemo(
    () => ({
      query: debouncedQuery,
      queryType,
      myLoans: false,
      pipelineType: PipeLineType.Default,
      crossPipeline: true,
      orderBy: 'relevance',
      orderDir: '1',
      count: PAGE_SIZE,
    }),
    [debouncedQuery, queryType],
  )

  useEffect(() => {
    requestKey.current = `${queryType}:${debouncedQuery}`

    if (!debouncedQuery) {
      setLoans([])
      setTotal(0)
      setLoading(false)
      return
    }

    const key = requestKey.current
    setLoading(true)

    filterPipeline({ ...searchFilters, skip: 0 })
      .then((res) => {
        if (requestKey.current !== key) return
        setLoans(normalizePipelineLoans(res))
        setTotal(Number(res.total) || 0)
      })
      .finally(() => {
        if (requestKey.current === key) setLoading(false)
      })
  }, [debouncedQuery, searchFilters, queryType])

  const onLoadMore = useCallback(() => {
    if (loading || loadingMore) return

    const key = requestKey.current
    setLoadingMore(true)

    filterPipeline({ ...searchFilters, skip: loans.length })
      .then((res) => {
        if (requestKey.current !== key) return
        const page = normalizePipelineLoans(res)
        // Paging reads the book a page at a time, so a loan boarded between two requests can
        // shift a row across the boundary and repeat. Keep the first copy of each loan.
        setLoans((prev) => {
          const seen = new Set(prev.map((loan) => loan.no))
          return [...prev, ...page.filter((loan) => !seen.has(loan.no))]
        })
        setTotal(Number(res.total) || 0)
      })
      .finally(() => {
        if (requestKey.current === key) setLoadingMore(false)
      })
  }, [loading, loadingMore, loans.length, searchFilters])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const onSelectLoan = (loan: Record<string, any>) => {
    setIsOpen(false)
    setQuery('')
    setDebouncedQuery('')
    // Results span both books, so the view dropdown decides where the loan opens: Origination
    // goes to the origination file, any other view (Servicing, FCI) goes to the servicing file.
    // Two exceptions fall back to where the loan actually lives: no view picked yet (the user
    // has not loaded a pipeline this session), and a loan that is not serviced at all, which
    // has no servicing file to open.
    const openInServicing = pipelineType
      ? pipelineType !== PipeLineType.Default && isServicingLoan(loan)
      : isServicingLoan(loan)
    history.push(openInServicing ? `/servicing/${loan.no}?menu=servicingNotes` : `/loan_process/overview/${loan.no}`)
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || !query.trim()) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (!loans.length) return
      setActiveIndex((prev) => Math.min(prev + 1, loans.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (!loans.length) return
      setActiveIndex((prev) => Math.max(prev - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      if (loans[activeIndex]) onSelectLoan(loans[activeIndex])
    } else if (event.key === 'Escape') {
      setIsOpen(false)
      inputRef.current?.blur()
    }
  }

  const showResults = isOpen && query.trim().length > 0

  return (
    <div className={`GlobalLoanSearch-container relative ${className}`} ref={containerRef}>
      <div className="relative">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Search..."
          value={query}
          className="block w-full rounded-full border-0 bg-slate-100 py-1.5 pl-8 pr-8 text-sm text-gray-800 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-200 [&::-webkit-search-cancel-button]:hidden"
          onChange={(event) => {
            setQuery(event.target.value)
            setIsOpen(true)
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={onKeyDown}
        />
        {query && (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600"
            onMouseDown={(event) => {
              event.preventDefault()
              setQuery('')
              setDebouncedQuery('')
              setLoans([])
              setTotal(0)
              setIsOpen(false)
              inputRef.current?.focus()
            }}
            aria-label="Clear search"
          >
            <XMarkIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {showResults && (
        <div className="absolute left-0 z-[60] mt-2 w-[min(28rem,calc(100vw-2rem))]">
          <LoanSearchResults
            query={query}
            queryType={queryType}
            loans={loans}
            total={total}
            loading={loading}
            loadingMore={loadingMore}
            activeIndex={activeIndex}
            onSelect={onSelectLoan}
            onLoadMore={onLoadMore}
            onQueryTypeChange={setQueryType}
            onActiveIndexChange={setActiveIndex}
          />
        </div>
      )}
    </div>
  )
}
